<template>
    <div class="task-details container-md pb-4">
        <b-button
            variant="link"
            class="top-link d-block d-md-none w-100 text-left"
            @click="unsetTask"
        >
            <i class="fas fa-chevron-left mr-2"></i>
            Back To List
        </b-button>

        <div v-if="loadingDetails || theTask == false" class="module p-4">
            <b-row class="skeleton">
                <b-col md="12" class="content">
                    <div class="smaller"></div>
                    <div class="smaller"></div>
                    <div></div>
                    <div></div>
                    <div class="smaller"></div>
                    <div class="smaller"></div>
                    <div></div>
                    <div class="smaller"></div>
                </b-col>
            </b-row>
        </div>
        <div v-else class="details form-inline mt-4">
            <div class="form-group row">
                <label class="col-4 col-form-label">Status:</label>
                <div class="col">
                    <span v-if="hasBeenDeleted" class="alert alert-danger p-1">
                        Deleted
                    </span>
                    <span
                        v-else-if="theTask.status == 1"
                        class="alert alert-info p-1"
                        >Completed</span
                    >
                    <span
                        v-else-if="theTask.status == 2"
                        class="alert alert-warning p-1"
                    >
                        In Progress
                    </span>
                    <span v-else class="alert alert-success p-1">
                        Open
                    </span>
                </div>
            </div>
            <div class="form-group row">
                <label class="col-4 col-form-label">Priority:</label>
                <div class="col">
                    <i class="cus-exclamation mr-0" v-if="theTask.priority"></i>
                    {{ theTask.priority == 1 ? "High" : "Normal" }}
                </div>
            </div>
            <div class="form-group row">
                <label class="col-4 col-form-label">Task:</label>
                <div class="col" v-html="updateTextLink(theTask.content)"></div>
            </div>
            <div class="form-group row">
                <label class="col-4 col-form-label">Assigned To:</label>
                <div class="col">{{ getAssignedTo() }}</div>
            </div>
            <div class="form-group row">
                <label class="col-4 col-form-label">Due:</label>
                <div class="col">
                    {{ _carbonDateTimeDisplay(theTask.date) }}
                </div>
            </div>
            <div
                class="form-group row"
                v-if="
                    theTask.client &&
                    theTask.client.businesses[0].pivot.relation_type ==
                        RELATIONS.ID_CLIENT_VENDOR
                "
            >
                <label class="col-4 col-form-label">Client (ID):</label>
                <div class="col">
                    <client-name
                        :client="theTask.client"
                        :text="getClientName()"
                    />
                </div>
            </div>
            <div
                class="form-group row"
                v-else-if="
                    theTask.client &&
                    theTask.client.businesses[0].pivot.relation_type ==
                        RELATIONS.ID_CONTACT_CLINIC
                "
            >
                <label class="col-4 col-form-label">Contact:</label>
                <div class="col">
                    <contact-name
                        :contact="theTask.client"
                        type="actor"
                    ></contact-name>
                </div>
            </div>
            <div class="form-group row" v-if="theTask.patient">
                <label class="col-4 col-form-label">Patient (ID):</label>
                <div class="col">
                    <patient-name
                        :patient="theTask.patient"
                        :text="
                            theTask.patient.name +
                            ' (' +
                            theTask.patient.index +
                            ')'
                        "
                    />
                </div>
            </div>
            <div class="form-group row" v-if="theTask.client">
                <label class="col-4 col-form-label">Mobile:</label>
                <div class="col">
                    <span v-if="theTask.client && theTask.client.mobile">
                        ({{ theTask.client.mobile_country_code }})
                        {{ theTask.client.mobile }}
                    </span>
                </div>
            </div>
            <div class="form-group row" v-if="theTask.client">
                <label class="col-4 col-form-label">Email:</label>
                <div class="col">
                    <span v-if="theTask.client && theTask.client.email">
                        {{ theTask.client.email }}
                    </span>
                </div>
            </div>
            <div
                class="form-group row"
                v-if="
                    theTask.task_profile &&
                    theTask.task_profile.items.length > 0
                "
            >
                <label class="col-4 col-form-label">Item(s):</label>
                <div class="col">
                    <div>
                        <span
                            v-for="(item, i) in theTask.task_profile.items"
                            :key="item.id + i"
                        >
                            <span v-if="i !== 0">, </span>
                            <a
                                :href="
                                    env.APPS +
                                    'inventory/item/view/' +
                                    item.encoded_id
                                "
                                target="_blank"
                            >
                                {{ item.display_name }}
                            </a>
                        </span>
                    </div>
                </div>
            </div>
            <div class="form-group row">
                <label class="col-4 col-form-label">Repeat:</label>
                <div class="col">
                    <span
                        v-if="
                            theTask.task_profile &&
                            theTask.task_profile.repeat_every
                        "
                    >
                        {{ getRepeat() }}
                    </span>
                </div>
            </div>
            <div class="form-group row">
                <label class="col-4 col-form-label">Notes:</label>
                <div
                    v-if="theTask.note"
                    class="col"
                    v-html="nl2br(theTask.note)"
                ></div>
            </div>

            <div class="mt-2" v-if="!hasBeenDeleted && theTask.status != 1">
                <button
                    id="btn-in-progress"
                    type="button"
                    v-if="canUpdateStatus() && theTask.status == 0"
                    v-b-tooltip.hover.top
                    title="Mark In Progress"
                    class="btn btn-secondary mr-1"
                    @click="updateTaskStatus(2)"
                >
                    <span class="fa-stack">
                        <i class="fas fa-spinner"></i>
                        <i class="fas fa-check fa-stack-1x"></i>
                    </span>
                </button>
                <button
                    v-if="canUpdateStatus()"
                    type="button"
                    class="btn btn-secondary mr-1"
                    v-b-tooltip.hover.top
                    title="Complete Task"
                    @click="updateTaskStatus(1)"
                >
                    <span class="fas fa-check"></span>
                </button>
                <button
                    v-if="canEditTask()"
                    type="button"
                    class="btn btn-secondary mr-1"
                    v-b-tooltip.hover.top
                    title="Edit Task"
                    @click="$refs.modalEditTask.open()"
                >
                    <span class="fas fa-edit"></span>
                </button>
                <button
                    v-else-if="canReassignTask()"
                    type="button"
                    class="btn btn-secondary mr-1"
                    v-b-tooltip.hover.top
                    title="Reassign Task"
                    @click="$refs.modalReassignTask.open()"
                >
                    <span class="fas fa-edit"></span>
                </button>
                <button
                    v-if="canDeleteTask()"
                    type="button"
                    class="btn btn-secondary"
                    v-b-tooltip.hover.top
                    title="Delete Task"
                    @click="showDelete()"
                >
                    <span class="fas fa-trash-alt"></span>
                </button>
            </div>
        </div>

        <div v-if="loadingDetails" class="module p-4">
            <hr />
            <b-row class="skeleton">
                <b-col md="12" class="content">
                    <div></div>
                    <div class="smaller"></div>
                    <div class="smaller"></div>
                </b-col>
            </b-row>
        </div>
        <div
            v-else
            class="logs"
            v-for="(log, i) in theTask.task_history"
            :key="'log' + i"
        >
            <hr />
            {{ _carbonDateTimeDisplay(log.create_at) }} -
            <span v-if="i === theTask.task_history.length - 1">Created</span>
            <span v-else>Edited</span> by
            {{ log.create_by ? log.create_by.label : "System" }}
            <div>
                <span> - </span>
                <span v-html="log.note"></span>
            </div>
        </div>

        <modal-update-task ref="modalEditTask" :is-edit="true" />

        <modal-update-task
            @reload="successUpdate()"
            ref="modalReassignTask"
            :is-reassign="true"
        />

        <modal-delete
            @reload="successDelete()"
            modal-id="modalDeleteTask"
            :task-selected="theTask"
        />
    </div>
</template>
<script>
import Tasks from "@/services/client-tasks";
import TextMixins from "@/mixins/text";
import CONSTANTS from "@/constants/tasks";
import CRELATIONS from "@/constants/relations";
import { forEach, isEmpty, clone } from "lodash";

export default {
    components: {
        ClientName: () => import("@/components/commons/client-name"),
        PatientName: () => import("@/components/commons/patient-name"),
        ContactName: () => import("@/components/commons/contact-name"),
        ModalUpdateTask: () => import("@/components/tasks/task-update-modal"),
        ModalDelete: () => import("@/components/tasks/task-delete-modal")
    },
    mixins: [TextMixins],
    data() {
        return {
            RELATIONS: CRELATIONS.TYPE,
            isLoading: false,
            logs: [
                {
                    date: "08/05/21 8:10AM",
                    pic: "Janet Green",
                    actions: ["Updated notes"]
                },
                {
                    date: "08/05/21 8:10AM",
                    pic: "Sagi Solomon",
                    actions: [
                        "Reassigned to technicians, veterinarians",
                        "Changed due date to 08/05/21"
                    ]
                },
                {
                    date: "08/05/21 8:10AM",
                    pic: "Sagi Solomon",
                    actions: []
                }
            ]
        };
    },
    computed: {
        theTask() {
            let info = clone(this.$store.getters["task/selected"]);
            // assign data if only it is not false
            if (info !== false) {
                info.tblTaskProfile = info.task_profile;
            }

            return info;
        },
        taskPermissions() {
            return this.$store.getters["task/taskPermissions"];
        },
        selectedDate() {
            return this.$store.getters["task/selectedDate"];
        },
        loadingDetails() {
            return this.$store.getters["task/loadingDetails"];
        },
        hasBeenDeleted() {
            if (!this.theTask) {
                return false;
            }

            if (this.theTask?.task_profile?.isactive === false) {
                return true;
            }

            return false;
        }
    },
    methods: {
        getAssignedTo() {
            let assignedTo = [];
            forEach(this.theTask.assign_to_roles, function (role) {
                assignedTo.push(role.roleName);
            });

            forEach(this.theTask.assign_to_staffs, function (staff) {
                assignedTo.push(staff.staffName);
            });

            return assignedTo.join(", ");
        },
        getClientName() {
            let client = this.theTask.client;
            let name = client.label;

            if (!isEmpty(client.businesses)) {
                name += " (" + client.businesses[0].pivot.index + ")";
            }

            return name;
        },
        updateTaskStatus(status) {
            let params = {
                id: this.theTask.id,
                currentStatus: this.theTask.status,
                status
            };

            Tasks.updateStatus(params)
                .then(response => {
                    if (response) {
                        if (response.message.code == 200) {
                            this._message(response.resource.message);
                            this.$store.dispatch(
                                "task/getTaskDetails",
                                this.theTask.id
                            );
                        } else {
                            this.showError();
                        }
                    } else {
                        this.showError();
                    }
                })
                .catch(error => {
                    this.showError();
                    console.error(error);
                });
        },
        showError() {
            this._message("Error : Unable to update task status", "danger");
        },
        showDelete() {
            this.$bvModal.show("modalDeleteTask");
        },
        successDelete() {
            this.$store.commit("task/unSetSelectedTask");
        },
        successUpdate() {
            this.$root.$emit("task-list:refresh");
            this.$store.dispatch("task/getTaskDetails", this.theTask.id);
        },
        canUpdateStatus() {
            if (
                this.taskPermissions.schedule_task_update ||
                this.theTask.is_created_by_me ||
                this.theTask.is_assigned_to_me
            ) {
                return true;
            }
            return false;
        },
        canEditTask() {
            if (
                this.taskPermissions.schedule_task_update ||
                this.theTask.is_created_by_me
            ) {
                return true;
            }
            return false;
        },
        canReassignTask() {
            if (
                this.taskPermissions.schedule_task_assign ||
                this.theTask.is_created_by_me
            ) {
                return true;
            }
            return false;
        },
        canDeleteTask() {
            if (
                this.taskPermissions.schedule_task_delete ||
                this.theTask.is_created_by_me
            ) {
                return true;
            }
            return false;
        },
        unsetTask() {
            this.$store.commit("task/unSetSelectedTask");
        },
        getRepeat() {
            return CONSTANTS.REPEAT_DESC[
                this.theTask.task_profile.repeat_every
            ];
        }
    }
};
</script>
<style type="text/css" scoped>
.task-details {
    background: white;
}
.top-link {
    background: #f7f7f7;
}

.task-details .details,
.logs {
    padding: 0 30px;
}

.details label {
    text-align: left;
    justify-content: normal;
}
.details .form-group {
    width: 100%;
    align-items: start;
}

.details .form-group label {
    padding-top: 0px;
    padding-bottom: 10px;
}

@media (min-width: 768px) {
    .task-details {
        min-height: 460px;
        max-height: calc(100vh - 150px);
        overflow: scroll;
    }
}
</style>
